import React, { useEffect, useRef, useState } from 'react'
import {
  useSequencerStore,
  selectCurrentProject,
} from '@/stores/sequencerStore'
import { QUANTIZE_SUBDIVISIONS, type QuantizeSubdivision } from '@/lib/quantize'

/**
 * Popover trigger + panel for the timing-quantize action. Transport
 * renders the trigger button; clicking it opens this popover
 * anchored to the button's wrapper.
 *
 * UX:
 *   - Subdivision pills (1, 1/2, 1/3, 1/4, 1/6, 1/8)
 *   - Strength slider (0..100%)
 *   - Scope toggle: Focused pad / All kit pads / All (+ MIDI)
 *   - Apply button — commits the snap; popover stays open so the
 *     user can try another setting without dismissing
 *
 * No destructive preview: the algorithm writes into the store
 * immediately on Apply. Undo is not yet wired (tracked under
 * Phase 3c). For now users can try different strengths and either
 * re-apply (compounds) or reload the pattern from memory to reset.
 */
export const QuantizePopover: React.FC = () => {
  const quantizePattern = useSequencerStore(s => s.quantizePattern)
  const trackerFocus = useSequencerStore(s => s.trackerFocus)
  // Pattern-scoped swing lives here now — the popover owns every
  // "timing feel" control (quantize snap + swing) so the transport
  // bar stays uncluttered. The slider reads / writes the active
  // pattern's swing field; no local mirror state because swing
  // applies live (unlike quantize which commits on Apply).
  const activeProject = useSequencerStore(selectCurrentProject)
  const swingPct = Math.round((activeProject?.swing ?? 0) * 100)
  const setPatternSwing = useSequencerStore(s => s.setPatternSwing)
  const [open, setOpen] = useState(false)
  const [subdivision, setSubdivision] = useState<QuantizeSubdivision>(4)
  const [strength, setStrength] = useState(1)
  const [scope, setScope] = useState<'focused' | 'allKit' | 'allIncludingMidi'>(
    'allKit',
  )
  const wrapRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click to match other Transport popovers.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  const apply = () => {
    quantizePattern({ subdivision, strength, scope })
  }

  const disableFocused = !trackerFocus
  // If the user had 'focused' selected but then closed the tracker /
  // lost focus, silently demote so the apply target makes sense.
  const effectiveScope =
    scope === 'focused' && disableFocused ? 'allKit' : scope

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Quantize — snap off-grid timing to a subdivision"
        className={`rounded-md border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors ${
          open
            ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
            : 'border-brd-subtle text-content-secondary hover:border-brd-emphasis'
        }`}
      >
        Quantize
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-1 z-50 w-80 rounded-lg border border-brd-subtle bg-surface-raised shadow-xl p-3 flex flex-col gap-3"
        >
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary mb-1">
              Subdivision
            </div>
            <div className="flex flex-wrap gap-1">
              {QUANTIZE_SUBDIVISIONS.map(s => {
                const active = s.value === subdivision
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSubdivision(s.value)}
                    className={`px-2 py-1 rounded text-[10px] font-mono border transition-colors ${
                      active
                        ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                        : 'bg-surface-sunken text-content-secondary border-brd-subtle hover:border-[color:var(--accent-halo)]'
                    }`}
                  >
                    {s.label}
                  </button>
                )
              })}
            </div>
          </div>

          <label className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
              <span>Strength</span>
              <span className="text-content-secondary normal-case tracking-normal">
                {Math.round(strength * 100)}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(strength * 100)}
              onChange={e => setStrength(Number(e.target.value) / 100)}
              className="w-full accent-[color:var(--accent)]"
            />
            <span className="text-[10px] text-content-tertiary">
              0% = no change · 100% = fully snapped to the chosen subdivision
            </span>
          </label>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary mb-1">
              Scope
            </div>
            <div className="flex flex-wrap gap-1">
              {(
                [
                  { v: 'focused' as const, label: 'Focused pad' },
                  { v: 'allKit' as const, label: 'All kit pads' },
                  {
                    v: 'allIncludingMidi' as const,
                    label: 'All + MIDI',
                  },
                ] as const
              ).map(opt => {
                const active = effectiveScope === opt.v
                const disabled = opt.v === 'focused' && disableFocused
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setScope(opt.v)}
                    disabled={disabled}
                    title={
                      disabled
                        ? 'Click a cell in the Tracker to enable focused quantize'
                        : undefined
                    }
                    className={`px-2 py-1 rounded text-[10px] font-mono border transition-colors disabled:opacity-40 ${
                      active
                        ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                        : 'bg-surface-sunken text-content-secondary border-brd-subtle hover:border-[color:var(--accent-halo)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Divider separates the "snap to grid" controls above
              from the "swing feel" control below — visually signals
              that Swing is live (no Apply) while Quantize is
              destructive (commits on Apply). */}
          <div className="h-px bg-brd-subtle" aria-hidden />

          <label className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
              <span>Swing</span>
              <span className="text-content-secondary normal-case tracking-normal">
                {swingPct}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={75}
              step={1}
              value={swingPct}
              onChange={e => setPatternSwing(Number(e.target.value) / 100)}
              className="w-full accent-[color:var(--accent)]"
              aria-label="Pattern swing percent"
            />
            <span className="text-[10px] text-content-tertiary">
              0% straight · 50% 16th-triplet feel · 75% shuffle. Applies live to
              the current pattern.
            </span>
          </label>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-2 py-1 text-[10px] font-mono text-content-tertiary hover:text-content-secondary"
            >
              Close
            </button>
            <button
              type="button"
              onClick={apply}
              className="px-3 py-1 rounded-md bg-accent text-[color:var(--accent-ink)] text-[10px] font-mono uppercase tracking-wider hover:bg-[color:var(--accent-hover)] transition-colors"
              title="Snap the currently configured scope's timings to the chosen subdivision"
            >
              Apply Quantize
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
